import "../../UserCollectedData.css";
import "./EditSearch.css";
import { IconButton, Modal } from "@mui/material";
import Box from "@mui/material/Box";
import PlanningApplicationAdvancedSearch from "@publicPortal/components/advancedSearch/PlanningApplicationAdvancedSearch";
import Board from "@publicPortal/components/board/Board";
import { PlanningApplicationSearchState } from "@publicPortal/pages/search/PlanningApplication/PlanningApplicationSearchState";
import PlanningApplicationsService from "@publicPortal/services/PlanningApplicationsService";
import { GetSavedSearchResponse, SavedSearchType } from "@shared/publicportal/contracts/ISavedSearch";
import { PlanningApplicationsFilter } from "@shared/publicportal/contracts/PlanningApplicationsFilter";
import React, { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PublicPortalState from "@publicPortal/utils/InitialState";
import UserCollectedDataHelper, { CriteriaValues } from "../../UserCollectedDataHelper";
import { IsStringNotEmptyValidator, ValidationResult } from "@shared/mirage/validators/IsStringNotEmptyValidator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import PlanningApplicationSimpleSearch from "@publicPortal/pages/search/PlanningApplication/PlanningApplicationSimpleSearch";
import MapFallback from "@mirage/components/MapView/components/MapFallback";
import SearchActions from "@publicPortal/pages/search/SearchActions";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { GAIA_DRAW_MODE, TileLayerOptions } from "@mirage/components/gaiaMap/GaiaMap";

const GaiaMap = lazy(() =>
    import("@mirage/components/gaiaMap/GaiaMap").then((module) => {
        return { default: module.GaiaMap };
    })
);

const PlanningApplicationEditSearch: React.FC<EditSavedSearch> = memo((props) => {
    const planningAppService = new PlanningApplicationsService();
    const initialValues = props.criteria.jsonSearchParameters && JSON.parse(props.criteria.jsonSearchParameters);
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { clientSettings } = sessionValue;
    const { initialSearchState, initialFilterState } = PublicPortalState.planningApplicationInitialSearchState();
    const initSearchState: PlanningApplicationSearchState = {
        ...initialSearchState,
        filter: {
            ...initialFilterState,
            ...initialValues,
        },
    } as PlanningApplicationSearchState;
    const [state, setState] = useState<PlanningApplicationSearchState>({} as PlanningApplicationSearchState);
    const [searchName, setSearchName] = useState("");
    const [searchNameValidation, setSearchNameValidation] = useState<ValidationResult>({ passed: true, error: "" });

    const handleSearchName = (value: string) => {
        const validationResult = IsStringNotEmptyValidator(value, "Enter Search name");
        setSearchNameValidation(validationResult);
        setSearchName(value);
    };

    useEffect(() => {
        setState(initSearchState);
        setSearchName(props.criteria.searchName);
        setSearchNameValidation({ passed: true, error: "" });
        fetchPicklistOptions();
    }, [props.criteria, props.closeModal]);

    const formActions = <SearchActions onSubmitHandler={handleSave} onCancelHandler={props.closeModal} customSubmitName={"Save"} customCancelName={"Cancel"} />;
    const onChange = useCallback(
        (geometry: string | null) => {
            setStateModifier({ geometry: geometry || "" });
        },
        [setStateModifier]
    );
    const masterLayer: TileLayerOptions | undefined = useMemo(
        () =>
            clientSettings
                ? {
                      layerUrl: clientSettings.MASTER_LAYER_URL,
                      attribution: clientSettings.MASTER_LAYER_ATTRIBUTION,
                  }
                : undefined,
        [clientSettings]
    );
    const enabledToolTypes = useMemo(() => [GAIA_DRAW_MODE.POLYGON], []);
    function getForm() {
        switch (initialValues?.searchType) {
            case "Simple": {
                return <PlanningApplicationSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <PlanningApplicationAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return (
                    <Suspense fallback={<MapFallback />}>
                        <GaiaMap geometryJson={state.filter.geometry} onChange={onChange} tileLayer={masterLayer} height={500} enabledToolTypes={enabledToolTypes} />
                    </Suspense>
                );
            }
            default:
                return <PlanningApplicationAdvancedSearch state={state} setter={setStateModifier} />;
        }
    }

    function getBoardStyles() {
        if (initialValues?.searchType === "Map") {
            const mapBoardStyles = {
                width: "75%",
            };

            return mapBoardStyles;
        }
    }

    const boardSize = initialValues?.searchType === "Advanced" ? "big" : "small";

    return (
        <>
            <Modal open={props.isOpen}>
                <Box height={"100%"}>
                    <Board id={boardSize === "big" ? "editSearch__bigBoard" : "editSearch__smallBoard"} size={boardSize} style={getBoardStyles()}>
                        <Box className="editSearch__headerContainer">
                            {UserCollectedDataHelper.getSearchName(searchName, handleSearchName, searchNameValidation)}
                            <IconButton onClick={props.closeModal} className="close-saved-search-modal-button">
                                <FontAwesomeIcon icon={faXmark} style={PublicPortalTheme.icons.sizeM} />
                            </IconButton>
                        </Box>
                        {getForm()}
                        {formActions}
                    </Board>
                </Box>
            </Modal>
        </>
    );

    async function handleSave() {
        if (searchNameValidation.passed) {
            await props.saveEditSearch(props.criteria.id, searchName, props.criteria.searchType, state.filter);
        }
    }

    function setStateModifier(modifier: Partial<PlanningApplicationsFilter>) {
        setState((prev) => {
            return { ...prev, filter: { ...prev.filter, ...modifier } };
        });
    }

    async function fetchPicklistOptions() {
        planningAppService.getFilteringLists().then((filter) => {
            setState((prev) => {
                return { ...prev, appTypes: filter.ApplicationTypes, decisions: filter.DecisionTypes, wards: filter.Wards, parishes: filter.Parishes };
            });
        });
    }
});

interface EditSavedSearch {
    criteria: GetSavedSearchResponse;
    isOpen: boolean;
    closeModal: () => void;
    saveEditSearch: (recordId: number, searchName: string, searchType: SavedSearchType, filter: CriteriaValues) => Promise<void>;
}

export default PlanningApplicationEditSearch;

export type { EditSavedSearch };
